import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  RefreshRounded,
  EditRounded,
  AddRounded,
  CloseRounded,
  SaveRounded,
} from "@mui/icons-material";
import api from "../api/api";

/**
 * Tavolata by IntegraNube
 * src/Pages/Salon.js
 *
 * Vista integral del salón conectada al backend real.
 *
 * Endpoints utilizados:
 *   GET    /restaurante/salon
 *   GET    /restaurante/mesas/:mesaId/pedido
 *   POST   /restaurante/mesas
 *   PATCH  /restaurante/mesas/:id
 *   DELETE /restaurante/mesas/:id
 *   POST   /restaurante/pedidos
 *
 * La autenticación, JWT y tenantid NO se manejan aquí:
 * los agrega automáticamente src/api/api.js.
 *
 * Importante:
 * - NO usa localStorage como fuente de verdad del plano.
 * - El estado operativo vuelve a consultarse desde la API.
 * - El modo edición persiste los campos reales de restaurante_mesas.
 * - posicion_x / posicion_y se interpretan como píxeles absolutos, igual que en BD.
 * - ancho/alto NO se persisten: se derivan automáticamente desde capacidad + forma.
 * - posicion_x / posicion_y sí se persisten en restaurante_mesas.
 */

const REFRESH_MS = 12000;

const STATUS = {
  DISPONIBLE: {
    label: "Disponible",
    color: "#16a34a",
    bg: "#dcfce7",
    text: "#166534",
    icon: "✓",
  },
  RESERVADA: {
    label: "Reservada",
    color: "#2563eb",
    bg: "#dbeafe",
    text: "#1e40af",
    icon: "R",
  },
  ESPERANDO_PEDIDO: {
    label: "Esperando pedido",
    color: "#ca8a04",
    bg: "#fef9c3",
    text: "#854d0e",
    icon: "…",
  },
  ABIERTA: {
    label: "Mesa abierta",
    color: "#d97706",
    bg: "#ffedd5",
    text: "#9a3412",
    icon: "A",
  },
  EN_COCINA: {
    label: "En cocina",
    color: "#ea580c",
    bg: "#ffedd5",
    text: "#9a3412",
    icon: "C",
  },
  OCUPADA: {
    label: "Ocupada",
    color: "#dc2626",
    bg: "#fee2e2",
    text: "#991b1b",
    icon: "●",
  },
  CUENTA_SOLICITADA: {
    label: "Solicitó cuenta",
    color: "#9333ea",
    bg: "#f3e8ff",
    text: "#6b21a8",
    icon: "$",
  },
  FUERA_SERVICIO: {
    label: "Fuera de servicio",
    color: "#475569",
    bg: "#e2e8f0",
    text: "#334155",
    icon: "×",
  },
};

const STATUS_ALIASES = {
  disponible: "DISPONIBLE",
  libre: "DISPONIBLE",
  reservada: "RESERVADA",
  reservado: "RESERVADA",
  esperando: "ESPERANDO_PEDIDO",
  esperando_pedido: "ESPERANDO_PEDIDO",
  abierto: "ABIERTA",
  abierta: "ABIERTA",
  pedido_abierto: "ABIERTA",
  cocina: "EN_COCINA",
  en_cocina: "EN_COCINA",
  preparando: "EN_COCINA",
  ocupada: "OCUPADA",
  ocupado: "OCUPADA",
  cuenta: "CUENTA_SOLICITADA",
  cuenta_solicitada: "CUENTA_SOLICITADA",
  bloqueada: "FUERA_SERVICIO",
  bloqueado: "FUERA_SERVICIO",
  fuera_servicio: "FUERA_SERVICIO",
};

function first(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return null;
}

function num(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function bool(value, fallback = true) {
  if (value === undefined || value === null) return fallback;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  const normalized = String(value).trim().toLowerCase();
  if (["true", "1", "si", "sí", "s", "yes"].includes(normalized)) return true;
  if (["false", "0", "no", "n"].includes(normalized)) return false;
  return fallback;
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function money(value) {
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(num(value, 0));
}

function normalizeStatus(raw, hasOrder = false) {
  const value = String(raw || "").trim();
  if (!value) return hasOrder ? "OCUPADA" : "DISPONIBLE";

  const upper = value.toUpperCase().replace(/[\s-]+/g, "_");
  if (STATUS[upper]) return upper;

  const lower = value.toLowerCase().replace(/[\s-]+/g, "_");
  if (STATUS_ALIASES[lower]) return STATUS_ALIASES[lower];

  if (upper.includes("CUENTA")) return "CUENTA_SOLICITADA";
  if (upper.includes("COCINA") || upper.includes("PREPAR")) return "EN_COCINA";
  if (upper.includes("RESERV")) return "RESERVADA";
  if (upper.includes("FUERA") || upper.includes("BLOQUE")) return "FUERA_SERVICIO";
  if (upper.includes("ABIER")) return "ABIERTA";
  if (upper.includes("OCUP")) return "OCUPADA";

  return hasOrder ? "OCUPADA" : "DISPONIBLE";
}


function normalizeShape(value) {
  const raw = String(value || "").trim().toUpperCase();

  if (["REDONDA", "ROUND", "CIRCULAR"].includes(raw)) return "round";
  if (["RECTANGULAR", "RECT", "RECTANGLE"].includes(raw)) return "rect";
  if (["BARRA", "BAR", "MESON", "MESÓN"].includes(raw)) return "bar";

  return "square";
}

function backendShape(value) {
  switch (normalizeShape(value)) {
    case "round":
      return "REDONDA";
    case "rect":
      return "RECTANGULAR";
    case "bar":
      return "BARRA";
    default:
      return "CUADRADA";
  }
}

function tableNumberFromRaw(raw, fallback) {
  const direct = first(raw?.numero, raw?.numero_mesa, raw?.nro);
  if (direct !== null) return num(direct, fallback);

  const codigo = String(first(raw?.codigo, "") || "");
  const match = codigo.match(/(\d+)(?!.*\d)/);
  return match ? num(match[1], fallback) : fallback;
}

function makeTableCode(number, tables = []) {
  const used = new Set(
    tables
      .map((table) =>
        String(first(table?.codigo, table?.raw?.codigo, "") || "")
          .trim()
          .toUpperCase()
      )
      .filter(Boolean)
  );

  const base = `MESA-${String(number).padStart(3, "0")}`;
  if (!used.has(base)) return base;

  let suffix = 2;
  while (used.has(`${base}-${suffix}`)) suffix += 1;
  return `${base}-${suffix}`;
}

function extractArray(payload, keys = []) {
  if (Array.isArray(payload)) return payload;

  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key];
    if (Array.isArray(payload?.data?.[key])) return payload.data[key];
    if (Array.isArray(payload?.result?.[key])) return payload.result[key];
  }

  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.rows)) return payload.rows;

  return [];
}

function unwrapObject(payload) {
  if (!payload || typeof payload !== "object") return null;
  if (payload.data && !Array.isArray(payload.data)) return payload.data;
  if (payload.result && !Array.isArray(payload.result)) return payload.result;
  return payload;
}

function normalizeZone(raw, index = 0) {
  const id = first(raw?.id, raw?.zona_id, raw?.zone_id, raw?.uuid, `zona-${index + 1}`);
  return {
    id: String(id),
    nombre: String(
      first(raw?.nombre, raw?.zona_nombre, raw?.name, raw?.descripcion, `Zona ${index + 1}`)
    ),
    descripcion: String(first(raw?.descripcion, raw?.detalle, raw?.subtitle, "") || ""),
    orden: num(first(raw?.orden, raw?.sort_order, raw?.posicion), index),
    activo: bool(first(raw?.activo, raw?.activa, raw?.habilitado), true),
  };
}

function defaultGridPosition(index) {
  const cols = 4;
  const col = index % cols;
  const row = Math.floor(index / cols);
  return {
    x: 100 + col * 180,
    y: 100 + row * 150,
  };
}


function visualSizeByCapacity(capacityValue, shapeValue) {
  const capacidad = Math.max(1, Math.min(num(capacityValue, 4), 50));
  const forma = normalizeShape(shapeValue);

  if (forma === "bar") {
    return {
      width: clamp(90 + capacidad * 25, 140, 520),
      height: 76,
    };
  }

  if (forma === "round") {
    const diameter = clamp(82 + capacidad * 8, 100, 220);
    return {
      width: diameter,
      height: diameter,
    };
  }

  if (forma === "rect") {
    return {
      width: clamp(85 + capacidad * 14, 125, 360),
      height: clamp(72 + Math.ceil(capacidad / 6) * 8, 80, 112),
    };
  }

  // Mesa cuadrada: crece suavemente según capacidad.
  const side = clamp(78 + capacidad * 7, 96, 180);
  return {
    width: side,
    height: side,
  };
}

function normalizeTable(raw, index = 0) {
  const pedido =
    raw?.pedido ||
    raw?.pedido_activo ||
    raw?.pedidoActual ||
    raw?.pedido_actual ||
    null;

  const pedidoId = first(
    raw?.pedido_id,
    raw?.pedido_activo_id,
    raw?.pedidoId,
    pedido?.id,
    pedido?.pedido_id
  );

  const fallbackPos = defaultGridPosition(index);

  const zonaId = first(
    raw?.zona_id,
    raw?.zonaId,
    raw?.zone_id,
    raw?.zona?.id,
    raw?.zona_uuid,
    "sin-zona"
  );

  const id = first(raw?.id, raw?.mesa_id, raw?.mesaId, raw?.uuid, index + 1);

  const personas = num(
    first(
      raw?.personas,
      raw?.cantidad_personas,
      raw?.comensales,
      pedido?.personas,
      pedido?.cantidad_personas,
      pedido?.comensales
    ),
    0
  );

  const total = num(
    first(
      raw?.total,
      raw?.total_actual,
      raw?.pedido_total,
      pedido?.total,
      pedido?.total_bruto,
      pedido?.monto_total
    ),
    0
  );

  const mesero = String(
    first(
      raw?.mesero_nombre,
      raw?.mesero,
      raw?.usuario_nombre,
      pedido?.mesero_nombre,
      pedido?.mesero,
      ""
    ) || ""
  );

  const minutos = num(
    first(
      raw?.minutos,
      raw?.minutos_abierta,
      raw?.minutos_ocupacion,
      raw?.minutos_ocupada,
      pedido?.minutos,
      pedido?.minutos_abierto
    ),
    0
  );

  const rawStatus = first(
    raw?.estado_operativo,
    raw?.estado_mesa,
    raw?.estado,
    raw?.pedido_estado,
    pedido?.estado_mesa,
    pedido?.estado
  );

  const capacidad = num(first(raw?.capacidad, raw?.capacidad_personas), 4);
  const forma = normalizeShape(first(raw?.forma, raw?.tipo_forma, "CUADRADA"));
  const visualSize = visualSizeByCapacity(capacidad, forma);

  return {
    id,
    codigo: String(first(raw?.codigo, raw?.mesa_codigo, "") || ""),
    numero: tableNumberFromRaw(raw, index + 1),
    nombre: String(first(raw?.nombre, raw?.mesa_nombre, `Mesa ${tableNumberFromRaw(raw, index + 1)}`)),
    zonaId: String(zonaId),
    zonaNombre: String(first(raw?.zona_nombre, raw?.zona?.nombre, "") || ""),
    capacidad,
    personas,
    forma,
    x: num(first(raw?.posicion_x, raw?.x), fallbackPos.x),
    y: num(first(raw?.posicion_y, raw?.y), fallbackPos.y),
    width: visualSize.width,
    height: visualSize.height,
    rotacion: num(first(raw?.rotacion, raw?.rotation), 0),
    estado: normalizeStatus(rawStatus, Boolean(pedidoId)),
    activo: bool(first(raw?.activo, raw?.activa), true),
    mesero,
    minutos,
    total,
    pedidoId,
    raw,
  };
}

function normalizeSalon(payload) {
  const root = unwrapObject(payload) || {};

  let rawZones = extractArray(root, ["zonas", "zones", "sectores"]);
  let rawTables = extractArray(root, ["mesas", "tables"]);

  // Algunos endpoints pueden devolver zonas con mesas anidadas.
  if (!rawTables.length && rawZones.length) {
    rawTables = rawZones.flatMap((zone) => {
      const nested = extractArray(zone, ["mesas", "tables"]);
      return nested.map((table) => ({
        ...table,
        zona_id: first(table?.zona_id, zone?.id, zone?.zona_id),
        zona_nombre: first(table?.zona_nombre, zone?.nombre, zone?.zona_nombre),
      }));
    });
  }

  // Si /salon devuelve filas planas, las aceptamos directamente.
  if (!rawTables.length && Array.isArray(payload)) rawTables = payload;

  const tables = rawTables.map(normalizeTable);

  // Si no llegó un arreglo explícito de zonas, se derivan desde las mesas.
  if (!rawZones.length) {
    const byId = new Map();
    tables.forEach((table) => {
      if (!byId.has(table.zonaId)) {
        byId.set(table.zonaId, {
          id: table.zonaId,
          nombre: table.zonaNombre || "Salón",
          descripcion: "",
          orden: byId.size,
          activo: true,
        });
      }
    });
    rawZones = Array.from(byId.values());
  }

  const zones = rawZones
    .map(normalizeZone)
    .filter((zone) => zone.activo)
    .sort((a, b) => a.orden - b.orden || a.nombre.localeCompare(b.nombre));

  return { zones, tables };
}

function normalizePedido(payload) {
  const root = unwrapObject(payload);
  if (!root) return null;

  // Caso: { pedido: {...} }
  const pedido =
    root?.pedido ||
    root?.pedido_activo ||
    root?.pedidoActual ||
    root?.pedido_actual ||
    root;

  if (!pedido || typeof pedido !== "object" || Array.isArray(pedido)) return null;

  const id = first(pedido?.id, pedido?.pedido_id, pedido?.pedidoId);
  if (!id) return null;

  return {
    ...pedido,
    id,
    estado: first(pedido?.estado, pedido?.estado_pedido, ""),
    personas: num(
      first(pedido?.personas, pedido?.cantidad_personas, pedido?.comensales),
      0
    ),
    total: num(
      first(pedido?.total, pedido?.total_bruto, pedido?.monto_total),
      0
    ),
  };
}

function errorMessage(error, fallback = "No fue posible completar la operación.") {
  return (
    error?.friendlyMessage ||
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  );
}

export default function Salon() {
  const planRef = useRef(null);
  const dragRef = useRef(null);
  const mountedRef = useRef(true);

  const [zones, setZones] = useState([]);
  const [tables, setTables] = useState([]);
  const [activeZone, setActiveZone] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const [editMode, setEditMode] = useState(false);
  const [dirtyIds, setDirtyIds] = useState(() => new Set());

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [notice, setNotice] = useState(null);

  const selected = useMemo(
    () => tables.find((table) => String(table.id) === String(selectedId)) || null,
    [tables, selectedId]
  );

  const visibleTables = useMemo(
    () =>
      tables
        .filter((table) => String(table.zonaId) === String(activeZone))
        .filter((table) => table.activo !== false),
    [tables, activeZone]
  );

  const activeZoneData = useMemo(
    () => zones.find((zone) => String(zone.id) === String(activeZone)) || null,
    [zones, activeZone]
  );

  const metrics = useMemo(() => {
    const current = visibleTables;
    const ocupadas = current.filter(
      (table) => !["DISPONIBLE", "RESERVADA", "FUERA_SERVICIO"].includes(table.estado)
    ).length;
    const disponibles = current.filter((table) => table.estado === "DISPONIBLE").length;
    const personas = current.reduce((sum, table) => sum + num(table.personas, 0), 0);

    return {
      total: current.length,
      ocupadas,
      disponibles,
      personas,
    };
  }, [visibleTables]);

  const showNotice = useCallback((type, message) => {
    setNotice({ type, message });
    window.clearTimeout(showNotice._timer);
    showNotice._timer = window.setTimeout(() => {
      if (mountedRef.current) setNotice(null);
    }, 4200);
  }, []);

  const loadSalon = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setLoading(true);
      else setRefreshing(true);

      try {
        // CONTRATO REAL:
        // GET /restaurante/salon
        // {
        //   zonas: [
        //     {
        //       id, nombre, descripcion, orden,
        //       mesas: [
        //         {
        //           id, zona_id, codigo, nombre, capacidad,
        //           posicion_x, posicion_y, forma,
        //           pedido_id, pedido_estado, comensales,
        //           total, estado, minutos_ocupada, ...
        //         }
        //       ]
        //     }
        //   ]
        // }
        const response = await api.get("/restaurante/salon");
        const normalized = normalizeSalon(response.data);

        if (!mountedRef.current) return;

        setZones(normalized.zones);

        setTables((current) => {
          // Mientras estamos editando, un refresh silencioso no debe pisar
          // las coordenadas locales que todavía no se han guardado.
          if (!editMode || dirtyIds.size === 0) {
            return normalized.tables;
          }

          const localById = new Map(
            current.map((table) => [String(table.id), table])
          );

          return normalized.tables.map((remote) =>
            dirtyIds.has(String(remote.id))
              ? localById.get(String(remote.id)) || remote
              : remote
          );
        });

        setActiveZone((current) => {
          const exists = normalized.zones.some(
            (zone) => String(zone.id) === String(current)
          );

          if (exists) return current;

          const firstZoneWithTables = normalized.zones.find((zone) =>
            normalized.tables.some(
              (table) => String(table.zonaId) === String(zone.id)
            )
          );

          return firstZoneWithTables?.id
            ? String(firstZoneWithTables.id)
            : normalized.zones[0]?.id
              ? String(normalized.zones[0].id)
              : "";
        });

        if (process.env.NODE_ENV !== "production") {
          console.debug("[Tavolata Salón] GET /restaurante/salon", {
            zonas: normalized.zones.length,
            mesas: normalized.tables.length,
            posiciones: normalized.tables.map((mesa) => ({
              id: mesa.id,
              codigo: mesa.codigo,
              x: mesa.x,
              y: mesa.y,
            })),
          });
        }
      } catch (error) {
        if (!silent) {
          showNotice("error", errorMessage(error, "No fue posible cargar el salón."));
        }
      } finally {
        if (mountedRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [dirtyIds, editMode, showNotice]
  );

  const loadTableOrder = useCallback(
    async (mesaId, { silent = false } = {}) => {
      if (!mesaId) return null;
      if (!silent) setDetailLoading(true);

      try {
        const response = await api.get(`/restaurante/mesas/${mesaId}/pedido`);
        const pedido = normalizePedido(response.data);

        if (mountedRef.current) setSelectedOrder(pedido);
        return pedido;
      } catch (error) {
        // Una mesa libre NO tiene pedido activo. El backend puede expresarlo
        // con 404/204 o con el código PEDIDO_ACTIVO_NO_ENCONTRADO.
        const status = error?.response?.status;
        const code = error?.response?.data?.code;

        if (
          status === 404 ||
          status === 204 ||
          code === "PEDIDO_ACTIVO_NO_ENCONTRADO"
        ) {
          if (mountedRef.current) setSelectedOrder(null);
          return null;
        }

        if (!silent) {
          showNotice("error", errorMessage(error, "No fue posible consultar el pedido."));
        }
        return null;
      } finally {
        if (mountedRef.current && !silent) setDetailLoading(false);
      }
    },
    [showNotice]
  );

  useEffect(() => {
    mountedRef.current = true;
    loadSalon();

    return () => {
      mountedRef.current = false;
      window.clearTimeout(showNotice._timer);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (editMode) return undefined;

    const interval = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        loadSalon({ silent: true });
        if (selectedId) loadTableOrder(selectedId, { silent: true });
      }
    }, REFRESH_MS);

    return () => window.clearInterval(interval);
  }, [editMode, loadSalon, loadTableOrder, selectedId]);

  useEffect(() => {
    if (!selectedId || editMode) {
      setSelectedOrder(null);
      return;
    }

    loadTableOrder(selectedId);
  }, [selectedId, editMode, loadTableOrder]);

  useEffect(() => {
    function handlePointerMove(event) {
      if (!dragRef.current || !planRef.current || !editMode) return;

      const rect = planRef.current.getBoundingClientRect();
      const id = dragRef.current.id;
      const width = num(dragRef.current.width, 100);
      const height = num(dragRef.current.height, 84);

      // Coordenada absoluta dentro del plano, incluyendo scroll.
      const x =
        event.clientX -
        rect.left +
        planRef.current.scrollLeft -
        dragRef.current.offsetX;

      const y =
        event.clientY -
        rect.top +
        planRef.current.scrollTop -
        dragRef.current.offsetY;

      const maxX = Math.max(
        18,
        planRef.current.scrollWidth - width - 18
      );

      const maxY = Math.max(
        28,
        planRef.current.scrollHeight - height - 28
      );

      setTables((current) =>
        current.map((item) =>
          String(item.id) === String(id)
            ? {
                ...item,
                x: Math.round(clamp(x, 18, maxX)),
                y: Math.round(clamp(y, 28, maxY)),
              }
            : item
        )
      );

      setDirtyIds((current) => {
        const next = new Set(current);
        next.add(String(id));
        return next;
      });
    }

    function handlePointerUp() {
      dragRef.current = null;
    }

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [editMode]);

  function startDrag(event, table) {
    if (!editMode || !planRef.current) return;

    event.preventDefault();
    event.stopPropagation();

    const rect = event.currentTarget.getBoundingClientRect();

    dragRef.current = {
      id: table.id,
      width: table.width,
      height: table.height,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
    };

    setSelectedId(table.id);
  }

  function patchSelectedLocal(patch) {
    if (!selected) return;

    setTables((current) =>
      current.map((table) => {
        if (String(table.id) !== String(selected.id)) return table;

        const next = { ...table, ...patch };

        if (
          Object.prototype.hasOwnProperty.call(patch, "capacidad") ||
          Object.prototype.hasOwnProperty.call(patch, "forma")
        ) {
          const size = visualSizeByCapacity(next.capacidad, next.forma);
          next.width = size.width;
          next.height = size.height;
        }

        return next;
      })
    );

    setDirtyIds((current) => {
      const next = new Set(current);
      next.add(String(selected.id));
      return next;
    });
  }

  async function saveLayout() {
    const changed = tables.filter((table) => dirtyIds.has(String(table.id)));
    if (!changed.length) {
      setEditMode(false);
      return;
    }

    setSaving(true);

    try {
      await Promise.all(
        changed.map((table) =>
          // CONTRATO REAL PATCH /restaurante/mesas/:id
          api.patch(`/restaurante/mesas/${table.id}`, {
            codigo: table.codigo || makeTableCode(table.numero, tables),
            nombre: table.nombre,
            zona_id: table.zonaId,
            capacidad: table.capacidad,
            forma: backendShape(table.forma),
            posicion_x: Math.round(num(table.x, 0)),
            posicion_y: Math.round(num(table.y, 0)),
            activo: table.activo !== false,
          })
        )
      );

      setDirtyIds(new Set());
      setEditMode(false);
      showNotice("success", "Plano del salón guardado correctamente.");
      await loadSalon({ silent: true });
    } catch (error) {
      showNotice("error", errorMessage(error, "No fue posible guardar el plano."));
    } finally {
      setSaving(false);
    }
  }

  async function cancelEdit() {
    setDirtyIds(new Set());
    setEditMode(false);
    setSelectedId(null);
    await loadSalon({ silent: true });
  }

  async function addTable() {
    if (!activeZone) return;

    const maxNumber = tables.reduce(
      (max, table) => Math.max(max, num(table.numero, 0)),
      0
    );

    setActionLoading(true);

    try {
      const newNumber = maxNumber + 1;
      const codigo = makeTableCode(newNumber, tables);

      const body = {
        codigo,
        nombre: `Mesa ${newNumber}`,
        zona_id: activeZone,
        capacidad: 4,
        posicion_x: 120,
        posicion_y: 120,
        forma: "CUADRADA",
        activo: true,
      };

      const response = await api.post("/restaurante/mesas", body);
      const created =
        response?.data?.mesa ||
        response?.data?.data ||
        response?.data;

      showNotice("success", "Mesa creada.");

      await loadSalon({ silent: true });

      const newId = first(created?.id, created?.mesa_id);
      if (newId) setSelectedId(newId);
    } catch (error) {
      showNotice("error", errorMessage(error, "No fue posible crear la mesa."));
    } finally {
      setActionLoading(false);
    }
  }

  async function removeSelected() {
    if (!selected) return;

    const confirmed = window.confirm(
      `¿Eliminar ${selected.nombre || `Mesa ${selected.numero}`} del salón?`
    );
    if (!confirmed) return;

    setActionLoading(true);

    try {
      await api.delete(`/restaurante/mesas/${selected.id}`);
      setSelectedId(null);
      showNotice("success", "Mesa eliminada.");
      await loadSalon({ silent: true });
    } catch (error) {
      showNotice("error", errorMessage(error, "No fue posible eliminar la mesa."));
    } finally {
      setActionLoading(false);
    }
  }

  async function openTable() {
    if (!selected || selected.estado !== "DISPONIBLE") return;

    const raw = window.prompt(
      `Cantidad de personas para ${selected.nombre}:`,
      String(Math.min(selected.capacidad || 2, 2))
    );

    if (raw === null) return;

    const personas = Math.max(1, Math.min(num(raw, 1), selected.capacidad || 99));

    setActionLoading(true);

    try {
      const response = await api.post("/restaurante/pedidos", {
        tipo: "MESA",
        mesa_id: selected.id,
        cantidad_personas: personas,
        personas,
      });

      const pedido =
        normalizePedido(response.data) ||
        normalizePedido(response.data?.pedido);

      showNotice("success", "Mesa abierta correctamente.");
      await loadSalon({ silent: true });

      if (pedido) setSelectedOrder(pedido);
      else await loadTableOrder(selected.id, { silent: true });
    } catch (error) {
      showNotice("error", errorMessage(error, "No fue posible abrir la mesa."));
    } finally {
      setActionLoading(false);
    }
  }

  function goToMesero() {
    if (!selected) return;

    const pedidoId = first(selectedOrder?.id, selected?.pedidoId);
    const params = new URLSearchParams();
    params.set("mesaId", selected.id);
    if (pedidoId) params.set("pedidoId", pedidoId);

    // Tavolata v2 navega mediante History API / rutas independientes.
    window.history.pushState({}, "", `/mesero?${params.toString()}`);
    window.dispatchEvent(new PopStateEvent("popstate"));window.dispatchEvent(new PopStateEvent("popstate"));
  }

  function goToCaja() {
    if (!selected) return;

    const pedidoId = first(selectedOrder?.id, selected?.pedidoId);
    const params = new URLSearchParams();
    params.set("mesaId", selected.id);
    if (pedidoId) params.set("pedidoId", pedidoId);

    window.history.pushState({}, "", `/caja?${params.toString()}`);
    window.dispatchEvent(new PopStateEvent("popstate"));
  }

  const currentOrderId = first(selectedOrder?.id, selected?.pedidoId);
  const displayPeople = first(
    selectedOrder?.personas,
    selected?.personas,
    0
  );
  const displayTotal = first(
    selectedOrder?.total,
    selected?.total,
    0
  );

  return (
    <div className="tv-shell">
      <style>{`
        * { box-sizing: border-box; }

        .tv-shell {
          min-height: 100vh;
          background:
            radial-gradient(circle at 10% 0%, rgba(22, 163, 74, .07), transparent 30%),
            #f7f8fa;
          color: #172033;
          font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont,
            "Segoe UI", sans-serif;
        }

        .tv-page {
          width: min(1600px, 100%);
          margin: 0 auto;
          padding: 22px;
        }

        .tv-toolbar {
          width: 100%;
          min-width: 0;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 14px;
        }

        .tv-actions {
          flex: 0 0 auto;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 8px;
          flex-wrap: nowrap;
          white-space: nowrap;
        }

        .tv-btn {
          height: 40px;
          border: 1px solid #d9dee7;
          background: #fff;
          color: #25324a;
          border-radius: 10px;
          padding: 0 14px;
          font-weight: 750;
          font-size: 13px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          white-space: nowrap;
        }

        .tv-btn .MuiSvgIcon-root {
          font-size: 18px;
          flex: 0 0 auto;
        }

        .tv-btn:hover:not(:disabled) { border-color: #aeb8c8; }
        .tv-btn:disabled { opacity: .55; cursor: default; }

        .tv-btn.primary {
          background: #166534;
          border-color: #166534;
          color: #fff;
        }

        .tv-btn.danger {
          border-color: #fecaca;
          color: #b91c1c;
        }

        .tv-refreshing {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          color: #64748b;
          font-size: 11px;
          font-weight: 700;
        }

        .tv-zones {
          flex: 1 1 auto;
          min-width: 0;
          display: flex;
          align-items: center;
          gap: 8px;
          margin: 0;
          overflow-x: auto;
          overflow-y: hidden;
          padding: 2px 2px 3px;
          overscroll-behavior-x: contain;
          scrollbar-width: thin;
        }

        .tv-zone {
          white-space: nowrap;
          border: 1px solid #dbe1e9;
          background: #fff;
          color: #526078;
          border-radius: 10px;
          padding: 10px 14px;
          font-size: 13px;
          font-weight: 750;
          cursor: pointer;
        }

        .tv-zone.active {
          background: #ecfdf3;
          border-color: #86efac;
          color: #166534;
        }

        .tv-metrics {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 10px;
          margin-bottom: 14px;
        }

        .tv-metric {
          background: rgba(255,255,255,.94);
          border: 1px solid #e2e7ee;
          border-radius: 12px;
          padding: 12px 14px;
          min-height: 66px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .tv-metric span {
          color: #718096;
          font-size: 12px;
          font-weight: 700;
        }

        .tv-metric strong {
          font-size: 23px;
          line-height: 1;
        }

        .tv-workspace {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 340px;
          gap: 14px;
          align-items: stretch;
        }

        .tv-card {
          background: #fff;
          border: 1px solid #e0e5ec;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 12px 28px rgba(15,23,42,.05);
        }

        .tv-plan-head {
          min-height: 55px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 12px 15px;
          border-bottom: 1px solid #e8ecf1;
        }

        .tv-plan-head strong { font-size: 15px; }

        .tv-plan-head small {
          display: block;
          margin-top: 3px;
          color: #8290a5;
          font-size: 11px;
        }

        .tv-edit-badge {
          border-radius: 999px;
          padding: 7px 10px;
          font-size: 11px;
          font-weight: 800;
          background: #eff6ff;
          color: #1d4ed8;
          border: 1px solid #bfdbfe;
        }

        .tv-plan {
          position: relative;
          min-width: 820px;
          height: clamp(520px, 65vh, 760px);
          overflow: auto;
          touch-action: none;
          background-color: #f9fafb;
          background-image:
            linear-gradient(#edf1f5 1px, transparent 1px),
            linear-gradient(90deg, #edf1f5 1px, transparent 1px),
            linear-gradient(#f4f6f8 1px, transparent 1px),
            linear-gradient(90deg, #f4f6f8 1px, transparent 1px);
          background-size: 48px 48px, 48px 48px, 12px 12px, 12px 12px;
        }

        .tv-plan::before {
          content: "";
          position: absolute;
          left: 2.5%;
          right: 2.5%;
          top: 3%;
          bottom: 3%;
          border: 8px solid #d9dee5;
          border-radius: 14px;
          pointer-events: none;
        }

        .tv-plan::after {
          content: "ENTRADA";
          position: absolute;
          left: 50%;
          bottom: 2px;
          transform: translateX(-50%);
          background: #fff;
          border: 1px solid #dce2e9;
          border-bottom: 0;
          padding: 6px 18px 5px;
          border-radius: 8px 8px 0 0;
          color: #78869b;
          font-size: 10px;
          font-weight: 900;
          letter-spacing: .12em;
          pointer-events: none;
        }

        .tv-table {
          position: absolute;
          user-select: none;
          cursor: pointer;
          transform-origin: center;
          transition: box-shadow .14s ease, transform .14s ease;
        }

        .tv-table:hover { z-index: 5; filter: brightness(.985); }
        .tv-table.editable { cursor: grab; }

        .tv-table-inner {
          width: 100%;
          height: 100%;
          border: 3px solid;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          text-align: center;
          padding: 8px;
          position: relative;
          box-shadow: 0 8px 16px rgba(15,23,42,.1);
        }

        .tv-table.round .tv-table-inner { border-radius: 50%; }
        .tv-table.square .tv-table-inner { border-radius: 15px; }
        .tv-table.rect .tv-table-inner { border-radius: 17px; }
        .tv-table.bar .tv-table-inner { border-radius: 9px; border-width: 2px; }

        .tv-table.selected .tv-table-inner {
          outline: 4px solid rgba(15,23,42,.13);
          outline-offset: 4px;
        }

        .tv-table-number {
          font-size: 18px;
          font-weight: 900;
          line-height: 1;
        }

        .tv-table-capacity {
          margin-top: 5px;
          color: #607086;
          font-size: 11px;
          font-weight: 750;
        }

        .tv-table-status {
          position: absolute;
          left: 50%;
          bottom: -10px;
          transform: translateX(-50%);
          min-width: max-content;
          border-radius: 999px;
          padding: 4px 7px;
          color: #fff;
          font-size: 9px;
          line-height: 1;
          font-weight: 900;
          box-shadow: 0 3px 8px rgba(15,23,42,.14);
        }

        .tv-dot {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          color: #fff;
          display: grid;
          place-items: center;
          position: absolute;
          top: -8px;
          right: -8px;
          border: 2px solid #fff;
          font-size: 10px;
          font-weight: 900;
          box-shadow: 0 2px 6px rgba(15,23,42,.18);
        }

        .tv-side {
          min-height: 100%;
        }

        .tv-empty {
          min-height: 100%;
          display: grid;
          place-items: center;
          text-align: center;
          padding: 34px;
          color: #8390a4;
        }

        .tv-empty strong {
          display: block;
          margin-bottom: 6px;
          color: #4b5a71;
        }

        .tv-side-head {
          padding: 18px;
          border-bottom: 1px solid #e9edf2;
        }

        .tv-side-head-top {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 12px;
        }

        .tv-side-head h2 {
          margin: 0;
          font-size: 21px;
        }

        .tv-side-head p {
          margin: 5px 0 0;
          color: #7a879b;
          font-size: 12px;
        }

        .tv-close {
          width: 32px;
          height: 32px;
          border-radius: 9px;
          border: 1px solid #dde3eb;
          background: #fff;
          cursor: pointer;
          font-size: 18px;
          color: #64748b;
        }

        .tv-pill {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          margin-top: 13px;
          padding: 7px 9px;
          border-radius: 999px;
          border: 1px solid;
          font-size: 11px;
          font-weight: 900;
        }

        .tv-pill-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
        }

        .tv-side-body { padding: 16px 18px 18px; }

        .tv-info-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          margin-bottom: 16px;
        }

        .tv-info {
          padding: 11px;
          border: 1px solid #e7ebf0;
          border-radius: 11px;
          background: #fafbfc;
        }

        .tv-info span {
          display: block;
          margin-bottom: 4px;
          color: #8793a5;
          font-size: 10px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: .05em;
        }

        .tv-info strong { font-size: 14px; }

        .tv-total {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 14px;
          margin-bottom: 14px;
        }

        .tv-total span {
          display: block;
          color: #79879a;
          font-size: 11px;
          font-weight: 700;
        }

        .tv-total strong {
          display: block;
          margin-top: 3px;
          font-size: 25px;
        }

        .tv-stack { display: grid; gap: 8px; }

        .tv-action {
          width: 100%;
          min-height: 42px;
          border-radius: 10px;
          border: 1px solid #dfe5ec;
          background: #fff;
          color: #25324a;
          font-size: 13px;
          font-weight: 800;
          cursor: pointer;
          text-align: left;
          padding: 0 13px;
        }

        .tv-action.primary {
          background: #166534;
          border-color: #166534;
          color: #fff;
        }

        .tv-action:disabled {
          opacity: .55;
          cursor: default;
        }

        .tv-editor { display: grid; gap: 12px; }

        .tv-grid2 {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
        }

        .tv-field label {
          display: block;
          margin-bottom: 5px;
          color: #68768b;
          font-size: 11px;
          font-weight: 800;
        }

        .tv-field input,
        .tv-field select {
          width: 100%;
          height: 39px;
          border: 1px solid #dbe1e8;
          border-radius: 9px;
          background: #fff;
          padding: 0 10px;
          color: #273449;
          font-size: 13px;
          outline: none;
        }

        .tv-auto-size-note {
          margin-top: -2px;
          padding: 9px 11px;
          border: 1px solid #dbe5ef;
          border-radius: 10px;
          background: #f8fafc;
          color: #64748b;
          font-size: 12px;
          line-height: 1.35;
        }

        .tv-divider {
          height: 1px;
          margin: 4px 0;
          background: #e9edf2;
        }

        .tv-legend {
          display: flex;
          flex-wrap: wrap;
          gap: 7px;
          margin-top: 14px;
        }

        .tv-legend-item {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          border: 1px solid #e4e8ee;
          background: #fff;
          border-radius: 999px;
          padding: 6px 8px;
          color: #627087;
          font-size: 10px;
          font-weight: 800;
        }

        .tv-legend-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
        }

        .tv-notice {
          position: fixed;
          z-index: 1000;
          right: 20px;
          bottom: 20px;
          max-width: min(420px, calc(100vw - 40px));
          padding: 12px 15px;
          border-radius: 11px;
          color: #fff;
          font-size: 13px;
          font-weight: 750;
          box-shadow: 0 14px 30px rgba(15,23,42,.22);
        }

        .tv-notice.success { background: #166534; }
        .tv-notice.error { background: #b91c1c; }

        .tv-loading {
          min-height: 560px;
          display: grid;
          place-items: center;
          color: #64748b;
          font-weight: 750;
        }

        .tv-spinner {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          border: 3px solid #dbe3ec;
          border-top-color: #166534;
          animation: tv-spin .8s linear infinite;
          margin: 0 auto 10px;
        }

        @keyframes tv-spin { to { transform: rotate(360deg); } }

        @media (max-width: 1050px) {
          .tv-workspace { grid-template-columns: 1fr; }
          .tv-side { min-height: auto; }
        }

        @media (max-width: 760px) {
          .tv-page { padding: 12px; }

          .tv-toolbar {
            gap: 8px;
          }

          .tv-zones {
            gap: 6px;
          }

          .tv-zone {
            padding: 9px 11px;
          }

          .tv-actions {
            gap: 6px;
          }

          .tv-btn {
            height: 38px;
            padding: 0 11px;
          }

          .tv-metrics { grid-template-columns: repeat(2, minmax(0,1fr)); }
          .tv-plan { height: 560px; }
        }

        @media (max-width: 560px) {
          .tv-btn {
            width: 38px;
            padding: 0;
            font-size: 0;
          }

          .tv-btn .MuiSvgIcon-root {
            font-size: 20px;
          }

          .tv-refreshing {
            display: none;
          }
        }
      `}</style>

      <main className="tv-page">
        <div className="tv-toolbar">
          {zones.length > 0 ? (
            <div className="tv-zones" aria-label="Zonas del salón">
              {zones.map((zone) => (
                <button
                  key={zone.id}
                  className={`tv-zone ${String(activeZone) === String(zone.id) ? "active" : ""}`}
                  onClick={() => {
                    setActiveZone(String(zone.id));
                    setSelectedId(null);
                    setSelectedOrder(null);
                  }}
                >
                  {zone.nombre}
                </button>
              ))}
            </div>
          ) : (
            <div className="tv-zones" />
          )}

          <div className="tv-actions">
            {refreshing && <span className="tv-refreshing">Actualizando…</span>}

            {!editMode && (
              <button
                className="tv-btn"
                disabled={refreshing}
                onClick={() => loadSalon({ silent: true })}
                title="Actualizar salón"
              >
                <RefreshRounded />
                Actualizar
              </button>
            )}

            {editMode ? (
              <>
                <button
                  className="tv-btn"
                  disabled={actionLoading || saving}
                  onClick={addTable}
                  title="Agregar mesa"
                >
                  <AddRounded />
                  Agregar mesa
                </button>

                <button
                  className="tv-btn"
                  disabled={saving}
                  onClick={cancelEdit}
                  title="Cancelar edición"
                >
                  <CloseRounded />
                  Cancelar
                </button>

                <button
                  className="tv-btn primary"
                  disabled={saving || dirtyIds.size === 0}
                  onClick={saveLayout}
                  title="Guardar plano"
                >
                  <SaveRounded />
                  {saving
                    ? "Guardando…"
                    : `Guardar plano${dirtyIds.size ? ` (${dirtyIds.size})` : ""}`}
                </button>
              </>
            ) : (
              <button
                className="tv-btn"
                onClick={() => {
                  setEditMode(true);
                  setSelectedOrder(null);
                }}
                title="Editar salón"
              >
                <EditRounded />
                Editar salón
              </button>
            )}
          </div>
        </div>

        <div className="tv-metrics">
          <div className="tv-metric">
            <span>Mesas</span>
            <strong>{metrics.total}</strong>
          </div>
          <div className="tv-metric">
            <span>Ocupadas</span>
            <strong>{metrics.ocupadas}</strong>
          </div>
          <div className="tv-metric">
            <span>Disponibles</span>
            <strong>{metrics.disponibles}</strong>
          </div>
          <div className="tv-metric">
            <span>Personas</span>
            <strong>{metrics.personas}</strong>
          </div>
        </div>

        <div className="tv-workspace">
          <section className="tv-card">
            <div className="tv-plan-head">
              <div>
                <strong>{activeZoneData?.nombre || "Salón"}</strong>
                <small>
                  {activeZoneData?.descripcion ||
                    (editMode
                      ? "Configura físicamente las mesas."
                      : "Selecciona una mesa para ver su operación.")}
                </small>
              </div>

              {editMode && (
                <span className="tv-edit-badge">
                  Arrastra mesas para cambiar posición
                </span>
              )}
            </div>

            {loading ? (
              <div className="tv-loading">
                <div>
                  <div className="tv-spinner" />
                  Cargando salón…
                </div>
              </div>
            ) : (
              <div
                ref={planRef}
                className="tv-plan"
                onClick={() => {
                  setSelectedId(null);
                  setSelectedOrder(null);
                }}
              >
                {visibleTables.map((table) => {
                  const state = STATUS[table.estado] || STATUS.DISPONIBLE;

                  return (
                    <div
                      key={table.id}
                      className={[
                        "tv-table",
                        ["round", "square", "rect", "bar"].includes(table.forma)
                          ? table.forma
                          : "square",
                        editMode ? "editable" : "",
                        String(selectedId) === String(table.id) ? "selected" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      style={{
                        left: `${table.x}px`,
                        top: `${table.y}px`,
                        width: `${table.width}px`,
                        height: `${table.height}px`,
                        transform: `rotate(${num(table.rotacion, 0)}deg)`,
                      }}
                      onPointerDown={(event) => startDrag(event, table)}
                      onClick={(event) => {
                        event.stopPropagation();
                        setSelectedId(table.id);
                      }}
                    >
                      <div
                        className="tv-table-inner"
                        style={{
                          borderColor: state.color,
                          background: state.bg,
                        }}
                      >
                        <div
                          className="tv-dot"
                          style={{ background: state.color }}
                          title={state.label}
                        >
                          {state.icon}
                        </div>

                        <div className="tv-table-number">{table.numero}</div>

                        <div className="tv-table-capacity">
                          👥 {table.personas || 0}/{table.capacidad}
                        </div>

                        <div
                          className="tv-table-status"
                          style={{ background: state.color }}
                        >
                          {state.label}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <aside className="tv-card tv-side">
            {!selected ? (
              <div className="tv-empty">
                <div>
                  <strong>
                    {editMode ? "Selecciona una mesa" : "Selecciona una mesa del salón"}
                  </strong>
                  <span>
                    {editMode
                      ? "Podrás moverla y configurar sus dimensiones."
                      : "Se consultará su pedido activo directamente a la API."}
                  </span>
                </div>
              </div>
            ) : (
              <>
                <div className="tv-side-head">
                  <div className="tv-side-head-top">
                    <div>
                      <h2>{selected.nombre || `Mesa ${selected.numero}`}</h2>
                      <p>
                        {activeZoneData?.nombre || selected.zonaNombre || "Salón"} · capacidad{" "}
                        {selected.capacidad}
                      </p>
                    </div>

                    <button
                      className="tv-close"
                      onClick={() => {
                        setSelectedId(null);
                        setSelectedOrder(null);
                      }}
                    >
                      ×
                    </button>
                  </div>

                  <div
                    className="tv-pill"
                    style={{
                      background: (STATUS[selected.estado] || STATUS.DISPONIBLE).bg,
                      color: (STATUS[selected.estado] || STATUS.DISPONIBLE).text,
                      borderColor: (STATUS[selected.estado] || STATUS.DISPONIBLE).color,
                    }}
                  >
                    <span
                      className="tv-pill-dot"
                      style={{
                        background: (STATUS[selected.estado] || STATUS.DISPONIBLE).color,
                      }}
                    />
                    {(STATUS[selected.estado] || STATUS.DISPONIBLE).label}
                  </div>
                </div>

                <div className="tv-side-body">
                  {editMode ? (
                    <div className="tv-editor">
                      <div className="tv-grid2">
                        <div className="tv-field">
                          <label>Número</label>
                          <input
                            type="number"
                            min="1"
                            value={selected.numero}
                            onChange={(event) => {
                              const numero = num(event.target.value, selected.numero);
                              const automaticCode =
                                !selected.codigo ||
                                /^MESA-\d+(?:-\d+)?$/i.test(selected.codigo);

                              patchSelectedLocal({
                                numero,
                                codigo: automaticCode
                                  ? makeTableCode(
                                      numero,
                                      tables.filter(
                                        (table) => String(table.id) !== String(selected.id)
                                      )
                                    )
                                  : selected.codigo,
                                nombre:
                                  selected.nombre?.match(/^Mesa\s+\d+$/i)
                                    ? `Mesa ${numero}`
                                    : selected.nombre,
                              });
                            }}
                          />
                        </div>

                        <div className="tv-field">
                          <label>Capacidad</label>
                          <input
                            type="number"
                            min="1"
                            max="50"
                            value={selected.capacidad}
                            onChange={(event) =>
                              patchSelectedLocal({
                                capacidad: Math.max(1, num(event.target.value, 1)),
                              })
                            }
                          />
                        </div>
                      </div>

                      <div className="tv-field">
                        <label>Código</label>
                        <input
                          value={selected.codigo || ""}
                          onChange={(event) =>
                            patchSelectedLocal({
                              codigo: event.target.value.toUpperCase().replace(/\s+/g, "-"),
                            })
                          }
                        />
                      </div>

                      <div className="tv-field">
                        <label>Nombre</label>
                        <input
                          value={selected.nombre || ""}
                          onChange={(event) =>
                            patchSelectedLocal({ nombre: event.target.value })
                          }
                        />
                      </div>

                      <div className="tv-field">
                        <label>Zona</label>
                        <select
                          value={selected.zonaId}
                          onChange={(event) =>
                            patchSelectedLocal({ zonaId: event.target.value })
                          }
                        >
                          {zones.map((zone) => (
                            <option key={zone.id} value={zone.id}>
                              {zone.nombre}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="tv-field">
                        <label>Forma</label>
                        <select
                          value={selected.forma}
                          onChange={(event) =>
                            patchSelectedLocal({ forma: event.target.value })
                          }
                        >
                          <option value="round">Redonda</option>
                          <option value="square">Cuadrada</option>
                          <option value="rect">Rectangular</option>
                          <option value="bar">Barra / mesón</option>
                        </select>
                      </div>

                      <div className="tv-grid2">
                        <div className="tv-field">
                          <label>Tamaño automático</label>
                          <input
                            value={`${Math.round(selected.width)} × ${Math.round(selected.height)} px`}
                            readOnly
                            title="Se calcula automáticamente según capacidad y forma."
                          />
                        </div>

                        <div className="tv-field">
                          <label>Posición X / Y</label>
                          <input
                            value={`${Math.round(selected.x)} / ${Math.round(selected.y)} px`}
                            readOnly
                            title="Estas coordenadas sí se guardan en restaurante_mesas."
                          />
                        </div>
                      </div>

                      <div className="tv-auto-size-note">
                        El tamaño visual se calcula desde la capacidad y la forma.
                        No requiere columnas ancho/alto en la base de datos.
                      </div>

                      <div className="tv-field">
                        <label>Rotación</label>
                        <input
                          type="number"
                          min="-180"
                          max="180"
                          value={selected.rotacion}
                          onChange={(event) =>
                            patchSelectedLocal({
                              rotacion: clamp(num(event.target.value, 0), -180, 180),
                            })
                          }/>
                      </div>

                      <div className="tv-divider" />

                      <button
                        className="tv-btn danger"
                        disabled={actionLoading}
                        onClick={removeSelected}
                      >
                        Eliminar mesa
                      </button>
                    </div>
                  ) : detailLoading ? (
                    <div className="tv-loading" style={{ minHeight: 250 }}>
                      <div>
                        <div className="tv-spinner" />
                        Consultando pedido…
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="tv-info-grid">
                        <div className="tv-info">
                          <span>Personas</span>
                          <strong>
                            {displayPeople || 0}/{selected.capacidad}
                          </strong>
                        </div>

                        <div className="tv-info">
                          <span>Mesero</span>
                          <strong>{selected.mesero || "Sin asignar"}</strong>
                        </div>

                        <div className="tv-info">
                          <span>Tiempo</span>
                          <strong>
                            {selected.minutos ? `${selected.minutos} min` : "—"}
                          </strong>
                        </div>

                        <div className="tv-info">
                          <span>Pedido</span>
                          <strong>
                            {currentOrderId ? `#${currentOrderId}` : "Sin pedido"}
                          </strong>
                        </div>
                      </div>

                      <div className="tv-total">
                        <span>Total actual</span>
                        <strong>{money(displayTotal)}</strong>
                      </div>

                      <div className="tv-stack">
                        {selected.estado === "DISPONIBLE" && !currentOrderId ? (
                          <button
                            className="tv-action primary"
                            disabled={actionLoading}
                            onClick={openTable}
                          >
                            {actionLoading ? "Abriendo…" : "Abrir mesa"}
                          </button>
                        ) : (
                          <>
                            <button
                              className="tv-action primary"
                              onClick={goToMesero}
                            >
                              Ver / continuar pedido
                            </button>

                            <button
                              className="tv-action"
                              onClick={goToMesero}
                            >
                              ＋ Agregar productos
                            </button>

                            {selected.estado === "CUENTA_SOLICITADA" && (
                              <button
                                className="tv-action"
                                onClick={goToCaja}
                              >
                                Ir a caja
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </aside>
        </div>

        <div className="tv-legend">
          {Object.entries(STATUS).map(([key, value]) => (
            <div className="tv-legend-item" key={key}>
              <span
                className="tv-legend-dot"
                style={{ background: value.color }}
              />
              {value.label}
            </div>
          ))}
        </div>
      </main>

      {notice && (
        <div className={`tv-notice ${notice.type}`}>
          {notice.message}
        </div>
      )}
    </div>
  );
}